import React, { createContext, useState, useContext, useEffect } from 'react';

// Create the context
const TemplateContext = createContext();

// Initial template structure
const initialTemplate = {
  id: 'template-1',
  name: 'My Email Template',
  elements: [],
  styles: {
    backgroundColor: '#ffffff',
    width: '600px',
    fontFamily: 'Arial, sans-serif',
    color: '#000000',
  }
};

// Template provider component
export const TemplateProvider = ({ children }) => {
  // Load template from localStorage if available, otherwise use initial template
  const [template, setTemplate] = useState(() => {
    const savedTemplate = localStorage.getItem('emailTemplate');
    return savedTemplate ? JSON.parse(savedTemplate) : initialTemplate;
  });
  
  const [history, setHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);

  // Save template to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('emailTemplate', JSON.stringify(template));
  }, [template]);

  // Add a new element to the template
  const addElement = (element) => {
    const newTemplate = {
      ...template,
      elements: [...template.elements, { ...element, id: `element-${Date.now()}` }]
    };
    
    // Add to history
    addToHistory(newTemplate);
    setTemplate(newTemplate);
  };

  // Update an existing element
  const updateElement = (id, properties) => {
    const newTemplate = {
      ...template,
      elements: template.elements.map(el => 
        el.id === id ? { ...el, ...properties } : el
      )
    };
    
    // Add to history
    addToHistory(newTemplate);
    setTemplate(newTemplate);
  };

  // Remove an element
  const removeElement = (id) => {
    const newTemplate = {
      ...template,
      elements: template.elements.filter(el => el.id !== id)
    };
    
    // Add to history
    addToHistory(newTemplate);
    setTemplate(newTemplate);
  };

  // Duplicate an element
  const duplicateElement = (id) => {
    const elementToDuplicate = template.elements.find(el => el.id === id);
    if (!elementToDuplicate) return;
    
    // Create a copy with a new ID
    const duplicatedElement = {
      ...elementToDuplicate,
      id: `element-${Date.now()}`
    };
    
    // Find the index of the original element
    const originalIndex = template.elements.findIndex(el => el.id === id);
    
    // Insert the duplicated element after the original
    const newElements = [...template.elements];
    newElements.splice(originalIndex + 1, 0, duplicatedElement);
    
    const newTemplate = {
      ...template,
      elements: newElements
    };
    
    // Add to history
    addToHistory(newTemplate);
    setTemplate(newTemplate);
  };
  
  // Set element layout (full, left, right)
  const setElementLayout = (id, layout) => {
    const newTemplate = {
      ...template,
      elements: template.elements.map(el => 
        el.id === id ? { ...el, layout } : el
      )
    };
    
    // Add to history
    addToHistory(newTemplate);
    setTemplate(newTemplate);
  };

  // Move element up (alternative to drag and drop)
  const moveElementUp = (id) => {
    const index = template.elements.findIndex(el => el.id === id);
    if (index <= 0) return; // Already at the top
    
    const newElements = [...template.elements];
    const temp = newElements[index];
    newElements[index] = newElements[index - 1];
    newElements[index - 1] = temp;
    
    const newTemplate = {
      ...template,
      elements: newElements
    };
    
    // Add to history
    addToHistory(newTemplate);
    setTemplate(newTemplate);
  };
  
  // Move element down (alternative to drag and drop)
  const moveElementDown = (id) => {
    const index = template.elements.findIndex(el => el.id === id);
    if (index === -1 || index >= template.elements.length - 1) return; // Already at the bottom
    
    const newElements = [...template.elements];
    const temp = newElements[index];
    newElements[index] = newElements[index + 1];
    newElements[index + 1] = temp;
    
    const newTemplate = {
      ...template,
      elements: newElements
    };
    
    // Add to history
    addToHistory(newTemplate);
    setTemplate(newTemplate);
  };

  // Reorder elements
  const reorderElements = (startIndex, endIndex) => {
    const result = Array.from(template.elements);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);

    const newTemplate = {
      ...template,
      elements: result
    };
    
    // Add to history
    addToHistory(newTemplate);
    setTemplate(newTemplate);
  };

  // Update template styles
  const updateTemplateStyles = (styles) => {
    const newTemplate = {
      ...template,
      styles: { ...template.styles, ...styles }
    };
    
    // Add to history
    addToHistory(newTemplate);
    setTemplate(newTemplate);
  };

  // Generate HTML from template
  const generateHTML = () => {
    // Basic HTML generation - will be enhanced
    let html = `
<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${template.name}</title>
</head>
<body style="margin: 0; padding: 0; background-color: ${template.styles.backgroundColor};">
  <table border="0" cellpadding="0" cellspacing="0" width="${template.styles.width}" style="margin: 0 auto; font-family: ${template.styles.fontFamily}; color: ${template.styles.color};">
`;

    // Add elements
    template.elements.forEach(element => {
      switch (element.type) {
        case 'header':
          html += `
    <tr>
      <td style="padding: ${element.styles?.padding || '10px'}; background-color: ${element.styles?.backgroundColor || 'transparent'}; text-align: ${element.styles?.textAlign || 'left'};">
        <h1 style="margin: 0; font-size: ${element.styles?.fontSize || '24px'}; color: ${element.styles?.color || template.styles.color};">${element.content}</h1>
      </td>
    </tr>`;
          break;
        case 'text':
          html += `
    <tr>
      <td style="padding: ${element.styles?.padding || '10px'}; background-color: ${element.styles?.backgroundColor || 'transparent'}; text-align: ${element.styles?.textAlign || 'left'};">
        <p style="margin: 0; font-size: ${element.styles?.fontSize || '16px'}; color: ${element.styles?.color || template.styles.color};">${element.content}</p>
      </td>
    </tr>`;
          break;
        case 'image':
          html += `
    <tr>
      <td style="padding: ${element.styles?.padding || '10px'}; background-color: ${element.styles?.backgroundColor || 'transparent'}; text-align: ${element.styles?.textAlign || 'center'};">
        <img src="${element.src}" alt="${element.alt || ''}" width="${element.width || '100%'}" style="display: block; max-width: 100%;" />
      </td>
    </tr>`;
          break;
        case 'button':
          html += `
    <tr>
      <td style="padding: ${element.styles?.padding || '10px'}; background-color: ${element.styles?.backgroundColor || 'transparent'}; text-align: ${element.styles?.textAlign || 'center'};">
        <table border="0" cellpadding="0" cellspacing="0" style="margin: 0 auto;">
          <tr>
            <td style="background-color: ${element.styles?.buttonColor || '#5876ff'}; border-radius: ${element.styles?.borderRadius || '4px'}; padding: 10px 20px;">
              <a href="${element.href || '#'}" style="color: ${element.styles?.textColor || '#ffffff'}; text-decoration: none; display: inline-block; font-size: ${element.styles?.fontSize || '16px'};">${element.content}</a>
            </td>
          </tr>
        </table>
      </td>
    </tr>`;
          break;
        case 'spacer':
          html += `
    <tr>
      <td style="height: ${element.height || '20px'};"></td>
    </tr>`;
          break;
        case 'divider':
          html += `
    <tr>
      <td style="padding: ${element.styles?.padding || '10px 0'};">
        <hr style="border: none; border-top: 1px solid ${element.styles?.color || '#dddddd'}; margin: 0;" />
      </td>
    </tr>`;
          break;
        case 'footer':
          html += `
    <tr>
      <td style="padding: ${element.styles?.padding || '10px'}; background-color: ${element.styles?.backgroundColor || '#f5f5f5'}; text-align: ${element.styles?.textAlign || 'center'}; font-size: ${element.styles?.fontSize || '12px'}; color: ${element.styles?.color || '#666666'};">
        ${element.content}
        ${element.includeUnsubscribe ? '<p style="margin-top: 10px;"><a href="#" style="color: #666666; text-decoration: underline;">Unsubscribe</a></p>' : ''}
      </td>
    </tr>`;
          break;
        default:
          break;
      }
    });

    html += `
  </table>
</body>
</html>`;

    return html;
  };

  // History management
  const addToHistory = (newTemplate) => {
    // If we're not at the end of history, truncate
    if (historyIndex < history.length - 1) {
      const newHistory = history.slice(0, historyIndex + 1);
      setHistory([...newHistory, newTemplate]);
    } else {
      setHistory([...history, newTemplate]);
    }
    setHistoryIndex(historyIndex + 1);
  };

  const undo = () => {
    if (historyIndex > 0) {
      setHistoryIndex(historyIndex - 1);
      setTemplate(history[historyIndex - 1]);
    }
  };

  const redo = () => {
    if (historyIndex < history.length - 1) {
      setHistoryIndex(historyIndex + 1);
      setTemplate(history[historyIndex + 1]);
    }
  };

  // Reset template to initial state
  const resetTemplate = () => {
    const newTemplate = { ...initialTemplate };
    addToHistory(newTemplate);
    setTemplate(newTemplate);
  };

  // Save template with a new name
  const saveTemplate = (name) => {
    const newTemplate = { ...template, name };
    setTemplate(newTemplate);
    localStorage.setItem('emailTemplate', JSON.stringify(newTemplate));
  };
  
  // Parse uploaded HTML and convert to template elements
  const parseUploadedHTML = (htmlContent) => {
    try {
      // Create a temporary DOM element to parse the HTML
      const parser = new DOMParser();
      const doc = parser.parseFromString(htmlContent, 'text/html');
      const body = doc.body;
      
      // Extract elements from the HTML
      const newElements = [];
      let elementId = 1;
      
      // Extract title if present
      const titleElement = doc.querySelector('title');
      let templateName = 'Imported Template';
      if (titleElement && titleElement.textContent) {
        templateName = titleElement.textContent;
      }
      
      // Check if the HTML has a table structure (common in email templates)
      const mainTable = body.querySelector('table');
      if (mainTable) {
        // Process table rows
        const rows = mainTable.querySelectorAll('tr');
        rows.forEach(row => {
          const cells = row.querySelectorAll('td');
          cells.forEach(cell => {
            processNodeForElements(cell, newElements, elementId);
            elementId++;
          });
        });
      } else {
        // Process direct children if no table structure
        Array.from(body.children).forEach(node => {
          processNodeForElements(node, newElements, elementId);
          elementId++;
        });
      }
      
      // If no elements were created, create a text element with the HTML content
      if (newElements.length === 0) {
        // Create a simple text element with the HTML content
        newElements.push({
          id: `element-${Date.now()}-1`,
          type: 'text',
          content: htmlContent.replace(/<!DOCTYPE[^>]*>|<html[^>]*>|<\/html>|<head>.*?<\/head>|<body[^>]*>|<\/body>/gi, '').trim(),
          styles: {}
        });
      }
      
      // Create a new template with the parsed elements
      const newTemplate = {
        ...template,
        name: templateName,
        elements: newElements
      };
      
      // Add to history and update state
      addToHistory(newTemplate);
      setTemplate(newTemplate);
      
      return true;
    } catch (error) {
      console.error('Error parsing HTML:', error);
      return false;
    }
  };
  
  // Helper function to process nodes and extract elements
  const processNodeForElements = (node, elements, idCounter) => {
    // Skip script tags and other non-visible elements
    if (!node || ['SCRIPT', 'STYLE', 'META', 'LINK'].includes(node.tagName)) return;
    
    // Determine element type based on tag
    let type = 'text';
    let content = '';
    let styles = {};
    
    if (node.tagName === 'H1' || node.tagName === 'H2' || node.tagName === 'H3') {
      type = 'heading';
      content = node.innerText || node.textContent;
      if (node.tagName === 'H1') styles.level = 1;
      else if (node.tagName === 'H2') styles.level = 2;
      else styles.level = 3;
    } else if (node.tagName === 'P') {
      type = 'text';
      content = node.innerText || node.textContent;
    } else if (node.tagName === 'IMG') {
      type = 'image';
      content = node.src;
      if (node.width) styles.width = `${node.width}px`;
      if (node.height) styles.height = `${node.height}px`;
      if (node.alt) styles.alt = node.alt;
    } else if (node.tagName === 'A') {
      type = 'button';
      content = node.innerText || node.textContent;
      styles.url = node.href;
    } else if (node.tagName === 'HR') {
      type = 'divider';
    } else if (node.tagName === 'BR') {
      type = 'spacer';
      styles.height = '20px';
    } else if (node.tagName === 'DIV' || node.tagName === 'SECTION' || node.tagName === 'TD') {
      // For container elements, process their children individually
      if (node.childNodes.length > 0) {
        // Check for images first
        const img = node.querySelector('img');
        if (img) {
          type = 'image';
          content = img.src;
          if (img.width) styles.width = `${img.width}px`;
          if (img.height) styles.height = `${img.height}px`;
          if (img.alt) styles.alt = img.alt;
        } else {
          // Check if it contains mostly text
          const text = node.innerText || node.textContent;
          if (text && text.trim()) {
            type = 'text';
            content = text.trim();
          } else {
            // Skip empty containers
            return;
          }
        }
      } else {
        // Skip empty containers
        return;
      }
    } else if (node.nodeType === Node.TEXT_NODE) {
      // Handle text nodes
      const text = node.textContent.trim();
      if (text) {
        type = 'text';
        content = text;
      } else {
        // Skip empty text nodes
        return;
      }
    } else {
      // Default to text for unknown elements
      const text = node.innerText || node.textContent;
      if (text && text.trim()) {
        type = 'text';
        content = text.trim();
      } else {
        // Skip empty elements
        return;
      }
    }
    
    // Extract inline styles if present
    if (node.style) {
      if (node.style.color) styles.color = node.style.color;
      if (node.style.backgroundColor) styles.backgroundColor = node.style.backgroundColor;
      if (node.style.fontFamily) styles.fontFamily = node.style.fontFamily;
      if (node.style.fontSize) styles.fontSize = node.style.fontSize;
      if (node.style.textAlign) styles.textAlign = node.style.textAlign;
      if (node.style.padding) styles.padding = node.style.padding;
    }
    
    // Create the element if we have valid content
    if (type === 'text' || type === 'heading') {
      if (!content || content.trim() === '') return;
    }
    
    elements.push({
      id: `element-${Date.now()}-${idCounter}`,
      type,
      content,
      styles
    });
  };

  return (
    <TemplateContext.Provider
      value={{
        template,
        addElement,
        updateElement,
        removeElement,
        duplicateElement,
        setElementLayout,
        moveElementUp,
        moveElementDown,
        reorderElements,
        updateTemplateStyles,
        generateHTML,
        undo,
        redo,
        resetTemplate,
        saveTemplate,
        parseUploadedHTML,
        canUndo: historyIndex > 0,
        canRedo: historyIndex < history.length - 1
      }}
    >
      {children}
    </TemplateContext.Provider>
  );
};

// Custom hook to use the template context
export const useTemplate = () => {
  const context = useContext(TemplateContext);
  if (!context) {
    throw new Error('useTemplate must be used within a TemplateProvider');
  }
  return context;
};
